import { defineStore } from "pinia"
import { afnoFor, type AdSpot } from "~/utils/afno"

export const useUserStore = defineStore(
  "user",
  () => {
    const id = ref(null)
    const username = ref(null)
    const email = ref(null)
    const siteAccessList = ref([])
    const token = ref(null)
    const status = ref(null)
    const expired = ref(false)
    const trial = ref(false)
    const offeredTrialUpgrade = ref(false)
    const offeredReactivation = ref(false)
    const active = ref(false)
    const accessLevel = ref(null)
    const permissions = ref([])
    const canStream = ref(false)
    const canDownloadPornPlus = ref(false)
    const biller = ref(null)
    const joinedAt = ref(null)
    const loggedIn = ref(false)
    const siteId = ref(null)
    const siteName = ref(null)
    const siteShortName = ref(null)
    const ageConfirmed = ref(false)
    const camUrls = ref({})
    const trailerPlayCount = ref(0)
    const billerJoinEpochDismissed = ref(false)
    const saleSpecialDismissedDate = ref(null)
    const isSaleSpecialDismissed = ref(true)
    const redirectUrl = ref(null)
    const dismissalDate = new Date(saleSpecialDismissedDate.value)
    const currentDate = new Date()
    const reactions = ref([])
    const favoritesCount = ref(null)
    const notificationsCount = ref(null)
    const subscriptionsCount = ref(null)
    const hasLifetime = ref(null)
    const isGuest = ref(false)
    const trailerPlayCountUpdatedOnDate = ref(null)
    const coinsAvailable = ref(0)
    const requiresAgeVerification = ref(false)
    const country = ref(null)
    const region = ref(null)
    const compliance = ref(false)

    if (currentDate > dismissalDate) {
      clearSaleSpecialDismissal()
      isSaleSpecialDismissed.value = false
    } else {
      isSaleSpecialDismissed.value = true
    }

    const signedLinkToCamSite = computed(() => {
      if (camUrls.value?.streammate_urls?.root) {
        return camUrls.value.streammate_urls.root
      } else {
        return null
      }
    })

    const full = computed(() => active.value && !trial.value && !expired.value)
    const lifetime = computed(() => hasLifetime.value === true)
    const streaming = computed(() => accessLevel.value === "streaming")

    const confirmAge = () => {
      ageConfirmed.value = true
    }

    const offerTrialUpgrade = () => {
      offeredTrialUpgrade.value = true
    }

    const offerReactivation = () => {
      offeredReactivation.value = true
    }

    const loadCounts = async () => {
      if (favoritesCount.value === null) {
        const [
          { data: favorites },
          { data: notifications },
          { data: subscriptions },
        ] = await Promise.all([
          useApi("/api/members/favorites?count=true"),
          useApi("/api/members/notifications?count=true"),
          useApi("/api/members/subscriptions?count=true"),
        ])

        favoritesCount.value = favorites.value?.pagination?.totalItems || 0
        notificationsCount.value =
          notifications.value?.pagination?.totalItems || 0
        subscriptionsCount.value = subscriptions.value?.count || 0
      }
    }

    const loadFavoritesCount = async () => {
      const { $api } = useNuxtApp()
      const response = await $api("/api/members/favorites?count=true")
      favoritesCount.value = response.pagination.totalItems
    }

    const loadNotificationsCount = async () => {
      const { $api } = useNuxtApp()
      const response = await $api("/api/members/notifications?count=true")
      notificationsCount.value = response.pagination.totalItems
    }

    const loadSubscriptionsCount = async () => {
      const { $api } = useNuxtApp()
      const response = await $api("/api/members/subscriptions?count=true")
      subscriptionsCount.value = response.count
    }

    const { isMobile } = useResponsive()

    async function navigateToCamSite(
      target: "_self" | "_blank" = "_blank",
      placement: AdSpot | null = null,
    ) {
      // Open a placeholder synchronously so the eventual navigation stays
      // under the user-gesture context — pop-up blockers reject
      // window.open() called after an await.
      const popup =
        target === "_blank" ? window.open("about:blank", "_blank") : null

      // Resolve the placement's AFNO click-tracking code up front and send it
      // to the backend, so zuul mints the OneClick URL/token WITH the code.
      // Attributing the join/sale needs the code on the server-issued OneClick;
      // patching it onto the URL client-side only ever moved the click.
      // Key on the site being VIEWED (primetime sites.id from /api/sites, which
      // matches the AFNO sheet) — not member.site_id, a Stan id often null here.
      let afno: string | null = null
      if (placement) {
        const device = isMobile.value ? "mobile" : "desktop"
        const siteData = useSiteStore().siteData as { id?: number } | null
        afno = afnoFor(siteData?.id, placement, device)
      }

      let url: string | null = null

      try {
        const { $api } = useNuxtApp()
        const response = await $api("/api/members/cams", {
          query: afno ? { afno } : undefined,
        })
        url = response?.streammate_urls?.root ?? null
        // Intentionally NOT cached into camUrls: this URL is placement-specific
        // now, and signedLinkToCamSite (the failure fallback below, shared
        // across placements) would then navigate a failed fetch with another
        // placement's code and mis-attribute the click. The fallback stays the
        // placement-agnostic URL minted at login.
      } catch (error) {
        console.error("Failed to refresh OneClick cam URL:", error)
      }

      url = url ?? signedLinkToCamSite.value

      if (url) {
        if (popup) {
          popup.location.href = url
        } else {
          window.location.href = url
        }
      } else if (popup) {
        popup.close()
      }
    }

    const setAccessLevel = (level: string) => {
      accessLevel.value = level
      status.value = level
      expired.value = level === "expired"
      hasLifetime.value = level === "lifetime"
      trial.value = level === "trial"
      isGuest.value = level === "guest"
    }

    const loadUser = (data: any, setCookie = true) => {
      id.value = data.member.id
      username.value = data.member.username
      email.value = data.member.email
      status.value = data.member.status
      accessLevel.value = data.member.accessLevel
      trial.value = data.member.accessLevel === "trial"
      expired.value = data.member.accessLevel === "expired"
      active.value = data.member.accessLevel === "active"
      permissions.value = data.member.permissions
      canStream.value = data.member.canStream
      canDownloadPornPlus.value = data.member.canDownload
      biller.value = data.member.biller
      joinedAt.value = data.member.joinedAt
      siteId.value = data.member.site_id
      siteAccessList.value = data.member.siteAccessList
      siteName.value = data.member.siteName
      siteShortName.value = data.member.siteShortName
      ageConfirmed.value = true
      loggedIn.value = true
      reactions.value = data.reactions
      camUrls.value = data.cam_urls
      trailerPlayCount.value = 0
      trailerPlayCountUpdatedOnDate.value = null
      redirectUrl.value = data.redirect_url
      hasLifetime.value = data.member.hasLifetime
      isGuest.value = data.member.accessLevel === "guest"
      coinsAvailable.value = data.member.coinsAvailable || 0
      requiresAgeVerification.value = data.member.requiresAgeVerification
      compliance.value = data.member.compliance

      // Persist the member's login-time geo so ssr:false pages (members area),
      // which have no request headers, can still resolve country/region via
      // useGeoLocation. Only overwrite when the payload carries geo — some
      // endpoints (welcome, upgrade) build the serializer without it, and we
      // must not clobber a good value with null.
      if (data.member.country) {
        country.value = data.member.country
        region.value = data.member.region ?? null
      }

      if (setCookie) {
        const cookie = useCookie("i", {
          path: "/",
          maxAge: 60 * 60 * 24 * 7, // 7 days expiration
          // httpOnly: true, // Important for security
          secure: process.env.NODE_ENV === "production", // Only set 'Secure' in production
        })
        cookie.value = data.member.accessToken
        // Note: access level cookie (l) is set by the backend via
        // Set-Cookie header with HMAC signature to prevent tampering.
      }
    }

    async function hideMemberChannelsWelcomeModal() {
      const cookie = useCookie("nsamcwm", {
        path: "/",
        maxAge: 60 * 60 * 24 * 365, // 1 year expiration
        secure: process.env.NODE_ENV === "production", // Only set 'Secure' in production
      })

      cookie.value = new Date()
    }

    const shouldMemberChannelsWelcomeModalBeShown = computed(() => {
      const cookie = useCookie("nsamcwm")
      return cookie.value === null || cookie.value === undefined
    })

    async function login(
      provided_username: string,
      provided_password: string,
      captchaResponse?: string,
      gCountry?: string,
      gRegion?: string,
    ) {
      try {
        const headers = useRequestHeaders(["host"])
        const host = import.meta.server ? headers?.host : window.location.host

        const params: any = {
          username: provided_username,
          password: provided_password,
          captcha_response: captchaResponse || null,
        }

        if (gCountry) params.development_country_override = gCountry
        if (gRegion) params.development_region_override = gRegion

        const response = await fetch(`/api/session`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "X-Site": host,
          },
          body: JSON.stringify(params),
        })

        if (response.ok) {
          loadUser(await response.json())
        }
      } catch (error) {
        console.error("Login failed:", error.message)
        this.$reset()
      }
    }

    async function loginWithToken(t: string) {
      try {
        const headers = useRequestHeaders(["host"])
        const host = import.meta.server ? headers?.host : window.location.host
        const response = await fetch(`/api/session/token`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "X-Site": host,
          },
          body: JSON.stringify({
            token: t,
          }),
        })

        if (response.ok) {
          loadUser(await response.json())
          return { success: true, error: false }
        } else {
          return { error: "Invalid token" }
        }
      } catch (error) {
        console.error("Login failed:", error.message)
        this.$reset()
      }
    }

    async function refetchUser() {
      try {
        const headers = useRequestHeaders(["host"])
        const host = import.meta.server ? headers?.host : window.location.host
        const response = await fetch(`/api/members/${id.value}`, {
          headers: {
            "Content-Type": "application/json",
            "X-Site": host,
            Cookie: `i=${token.value}`,
          },
        })

        if (response.ok) {
          loadUser(await response.json())
        } else {
          return { error: "Invalid token" }
        }
      } catch (error) {
        console.error("Login failed:", error.message)
        this.$reset()
      }
    }

    async function storeUserFromGuestAccess(data: any) {
      loadUser(data)
      isGuest.value = true
      return data
    }

    function isSameDay(date1: Date, date2: Date): boolean {
      if (date1 === null) {
        return false
      }

      return (
        date1.getFullYear() === date2.getFullYear() &&
        date1.getMonth() === date2.getMonth() &&
        date1.getDate() === date2.getDate()
      )
    }

    function incrementTrailerPlayCount() {
      const now = new Date()

      if (
        trailerPlayCountUpdatedOnDate.value === null ||
        !isSameDay(new Date(trailerPlayCountUpdatedOnDate.value), now)
      ) {
        trailerPlayCount.value = 0
        trailerPlayCountUpdatedOnDate.value = now.toISOString()
      }

      trailerPlayCount.value++
    }

    function dismissBillerJoinEpoch() {
      billerJoinEpochDismissed.value = true
    }

    function dismissSaleSpecial(daysToExpire = 30) {
      const expiryDate = new Date()
      expiryDate.setDate(expiryDate.getDate() + daysToExpire)
      saleSpecialDismissedDate.value = expiryDate.toISOString()
      isSaleSpecialDismissed.value = true
    }

    function isAllowedToInteract() {
      return expired.value === false && trial.value === false
    }

    function clearSaleSpecialDismissal() {
      saleSpecialDismissedDate.value = null
      isSaleSpecialDismissed.value = false
    }

    function $reset() {
      id.value = null
      username.value = null
      email.value = null
      status.value = null
      accessLevel.value = null
      trial.value = false
      expired.value = false
      active.value = false
      permissions.value = []
      canStream.value = false
      canDownloadPornPlus.value = false
      biller.value = null
      joinedAt.value = null
      siteId.value = null
      siteAccessList.value = []
      siteName.value = null
      siteShortName.value = null
      loggedIn.value = false
      reactions.value = []
      camUrls.value = {}
      trailerPlayCount.value = 0
      redirectUrl.value = null
      favoritesCount.value = null
      notificationsCount.value = null
      subscriptionsCount.value = null
      hasLifetime.value = null
      isGuest.value = false
      requiresAgeVerification.value = false
      compliance.value = false
      country.value = null
      region.value = null

      const cookie = useCookie("i")
      cookie.value = null

      // Clear access level and CloudFront signed cookies
      const accessLevelCookie = useCookie("l")
      accessLevelCookie.value = null
      const cfPolicy = useCookie("CloudFront-Policy")
      cfPolicy.value = null
      const cfSignature = useCookie("CloudFront-Signature")
      cfSignature.value = null
      const cfKeyPairId = useCookie("CloudFront-Key-Pair-Id")
      cfKeyPairId.value = null
    }

    const submitAgeVerification = async (_data) => {
      const { $api } = useNuxtApp()
      await $api("/api/members/confirm_age", {
        method: "POST",
      })
    }

    const verifyAge = async () => {
      requiresAgeVerification.value = false
    }

    const setCountry = (c: string) => {
      country.value = c
    }

    const setRegion = (r: string) => {
      region.value = r
    }

    return {
      $reset,
      accessLevel,
      active,
      ageConfirmed,
      biller,
      billerJoinEpochDismissed,
      canDownloadPornPlus,
      canStream,
      confirmAge,
      dismissBillerJoinEpoch,
      dismissSaleSpecial,
      expired,
      favoritesCount,
      full,
      id,
      incrementTrailerPlayCount,
      isGuest,
      isSaleSpecialDismissed,
      joinedAt,
      loadCounts,
      loadFavoritesCount,
      loadNotificationsCount,
      loadSubscriptionsCount,
      loggedIn,
      login,
      loginWithToken,
      navigateToCamSite,
      notificationsCount,
      offerReactivation,
      offerTrialUpgrade,
      offeredTrialUpgrade,
      permissions,
      refetchUser,
      setAccessLevel,
      siteAccessList,
      siteId,
      siteName,
      siteShortName,
      subscriptionsCount,
      status,
      storeUserFromGuestAccess,
      token,
      trailerPlayCount,
      trailerPlayCountUpdatedOnDate,
      trial,
      username,
      email,
      camUrls,
      signedLinkToCamSite,
      redirectUrl,
      isAllowedToInteract,
      reactions,
      hasLifetime,
      lifetime,
      requiresAgeVerification,
      streaming,
      submitAgeVerification,
      verifyAge,
      country,
      region,
      setCountry,
      setRegion,
      compliance,
      shouldMemberChannelsWelcomeModalBeShown,
      hideMemberChannelsWelcomeModal,
      coinsAvailable,
    }
  },
  {
    persist: true,
  },
)
